import { v4 as uuidv4 } from "uuid";
import { StageType } from "../../../../../graphql/types";
import {
  BeefBreedingStageDataIntervention,
  BeefGrowingStageDataIntervention,
  BeefInputBreedingIntervention,
  BeefInputGrowingIntervention,
  BeefInputIntervention,
  BeefOutputBreedingCalvesIntervention,
  BeefOutputIntervention
} from "../../../../sustell_15/models/Intervention/BeefIntervention";
import {
  CattleBeddingSystem,
  CattleEmission,
  CattleFeed,
  CattleHousing,
  CattleHousingAndManure,
  CattleManureManagementSystem,
  ExternalSource,
  FreshGrass,
  InternalSource,
  MothersMilk
} from "../../../../sustell_15/models/Intervention/CattleIntervention";
import {
  BeefBreedingStageDataInterventionDBModel,
  BeefGrowingStageDataInterventionDBModel,
  BeefInputBreedingInterventionDBModel,
  BeefInputGrowingInterventionDBModel,
  BeefInterventionDBModel,
  BeefOutputBreedingCalvesDBModel,
  BeefOutputGrowingInterventionDBModel,
  BeefStageDBModel,
  EmissionsDBModel
} from "../../../../sustell_15/models/TempBackendModels/TempInterventionModels/TempBeefInterventionsDB";
import { createCoreForm, isValidValue, mapResourceUse } from "./inCommonMapperSustellV2";
import {
  CattleBeddingSystem as BaselineCattleBeddingSystem,
  CattleManureManagementSystemType
} from "../../../../sustell_15/models/Baseline/CattleBaseline";
import {
  CattleAnimalInputDBModel,
  CattleHousingAndManureDBModel,
  CattleHousingDBModel,
  CompoundFeedInfoDBModel,
  FeedingProgramCalfDBModel,
  FreshGrassDBModel,
  MothersMilkDBModel,
  SingleIngredientUseDBModel
} from "../../../../sustell_15/models/TempBackendModels/TempInterventionModels/TempCattleInterventionDB";
import { getDefaultTransportUnit, getUserTransportUnit, setInterventionFloatValue } from "../../Baseline/mapper/inCommonMapperSustellV2";
import { UserProfilePrefs } from "../../../Helpers/UserProfilePrefs";
import { defaultUnitsV2 } from "../../../../sustell_15/utils/unit-utils";
import { InterventionFeed, InterventionPropertyValue } from "../../../../sustell_15/models/Intervention/Intervention";
import { BaselineFeed, BaselineFeedWithOrigin } from "../../../../sustell_15/models/Baseline";
import { floatValueInDefaultUnit } from "../../Baseline/mapper/floatHelperFunctions";
import {
  CompoundFeedInfoDBModel as BaselineCompoundFeedInfoDBModel,
  SingleIngredientUseDBModel as BaselineSingleIngredientUseDBModel,
  FreshGrassDBModel as BaselineFreshGrassDBModel,
} from "../../../../sustell_15/models/TempBackendModels/TempCattleDB";

const getBaseInput = (): BeefInputIntervention => {
  return {
    startDate: "",
    startDate_newValue: "",
    startDate_changeMetric: "",
    cattleProductivity: "",
    cattleProductivity_newValue: "",
    cattleProductivity_changeMetric: "",
    averageWeightNewAnimals: "",
    averageWeightNewAnimals_newValue: "",
    averageWeightNewAnimals_changeMetric: "",
    internalSources: [],
    externalSources: [],
  };
};

const getGrowingInput = (): BeefInputGrowingIntervention => {
  return {
    endDate: "",
    endDate_newValue: "",
    endDate_changeMetric: "",
    isStageRepeated: "",
    isStageRepeated_newValue: "",
    isStageRepeated_changeMetric: "",
    growingOperation: "",
    growingOperation_newValue: "",
    growingOperation_changeMetric: "",
    numberOfRepetitions: "",
    numberOfRepetitions_newValue: "",
    numberOfRepetitions_changeMetric: "",
    growingPurpose: "",
    growingPurpose_newValue: "",
    growingPurpose_changeMetric: "",
    averageAgeAtStart: "",
    averageAgeAtStart_newValue: "",
    averageAgeAtStart_changeMetric: "",
    animalsPresentAtStart: "",
    animalsPresentAtStart_newValue: "",
    animalsPresentAtStart_changeMetric: "",
    animalsPresentAtEnd: "",
    animalsPresentAtEnd_newValue: "",
    animalsPresentAtEnd_changeMetric: "",
    averageWeightAtStart: "",
    averageWeightAtStart_newValue: "",
    averageWeightAtStart_changeMetric: "",
    averageWeightAtEnd: "",
    averageWeightAtEnd_newValue: "",
    averageWeightAtEnd_changeMetric: "",
    ...getBaseInput()
  };
};

const getBreedingInput = (): BeefInputBreedingIntervention => {
  return {
    animalsPresentAtStart: "",
    animalsPresentAtStart_newValue: "",
    animalsPresentAtStart_changeMetric: "",
    animalsPresentAtEnd: "",
    animalsPresentAtEnd_newValue: "",
    animalsPresentAtEnd_changeMetric: "",
    averageWeightAtStart: "",
    averageWeightAtStart_newValue: "",
    averageWeightAtStart_changeMetric: "",
    averageWeightAtBirth: "",
    averageWeightAtBirth_newValue: "",
    averageWeightAtBirth_changeMetric: "",
    permanencePeriod: "",
    permanencePeriod_newValue: "",
    permanencePeriod_changeMetric: "",
    ...getBaseInput()
  };
};

const getMothersMilk = (): MothersMilk => {
  return {
    isMothersMilk: false,
    isMothersMilk_newValue: "",
    isMothersMilk_changeMetric: "",

    amount: "",
    amount_newValue: "",
    amount_changeMetric: "",

    crudeProteinContent: "",
    crudeProteinContent_newValue: "",
    crudeProteinContent_changeMetric: "",

    digestibility: "",
    digestibility_newValue: "",
    digestibility_changeMetric: "",

    dryMatterContent: "",
    dryMatterContent_newValue: "",
    dryMatterContent_changeMetric: "",
  };
};

const getFeed = (): CattleFeed => {
  return {
    databaseFoundation: "",
    dietCharacterisation_newValue: "",
    dietCharacterisation_changeMetric: "",

    averageMilkFat: "",
    averageMilkFat_newValue: "",
    averageMilkFat_changeMetric: "",

    compoundFeeds: [],
    singleIngredients: [],
    freshGrass: [],

    compoundFeedsAdditions: [],
    singleIngredientsAdditions: [],
    freshGrassAdditions: [],
  };
};

const getHousingAndManure = (): CattleHousingAndManure => {
  return {
    timeInFeedlot: "",
    timeInFeedlot_newValue: "",
    timeInFeedlot_changeMetric: "",

    timeInHousing: "",
    timeInHousing_newValue: "",
    timeInHousing_changeMetric: "",

    timeGrazingLargeAreas: "",
    timeGrazingLargeAreas_newValue: "",
    timeGrazingLargeAreas_changeMetric: "",

    timeGrazingPastures: "",
    timeGrazingPastures_newValue: "",
    timeGrazingPastures_changeMetric: "",

    timeInHousingTied: "",
    timeInHousingTied_newValue: "",
    timeInHousingTied_changeMetric: "",

    manureSystems: [],
    beddingSystems: [],

    manureSystemsAdditions: [],
    beddingSystemsAdditions: [],
  };
};

const getHousing = (): CattleHousing => {
  return {
    beddingSystems: [],
    beddingSystemsAdditions: [],

    calves: getHousingAndManure(),
    cows: getHousingAndManure(),
  };
};

const getOutput = (): BeefOutputIntervention => {
  return {
    averageAge: "",
    averageAge_newValue: "",
    averageAge_changeMetric: "",
    cowsToAnotherStage: "",
    cowsToAnotherStage_newValue: "",
    cowsToAnotherStage_changeMetric: "",
    averageWeightOfCowsLeavingTheStage: "",
    averageWeightOfCowsLeavingTheStage_newValue: "",
    averageWeightOfCowsLeavingTheStage_changeMetric: "",
    cowsSold: "",
    cowsSold_newValue: "",
    cowsSold_changeMetric: "",
    pricePerCowSold: "",
    pricePerCowSold_newValue: "",
    pricePerCowSold_changeMetric: "",
    numberOfMortalitiesCows: "",
    numberOfMortalitiesCows_newValue: "",
    numberOfMortalitiesCows_changeMetric: "",
    averageWeightAtMortalityCows: "",
    averageWeightAtMortalityCows_newValue: "",
    averageWeightAtMortalityCows_changeMetric: "",
  };
};

const getOutputCalves = (): BeefOutputBreedingCalvesIntervention => {
  return {
    weanedCalvesToAnotherStage: "",
    weanedCalvesToAnotherStage_newValue: "",
    weanedCalvesToAnotherStage_changeMetric: "",
    averageWeightOfCalvesLeavingTheStage: "",
    averageWeightOfCalvesLeavingTheStage_newValue: "",
    averageWeightOfCalvesLeavingTheStage_changeMetric: "",
    weanedCalvesSold: "",
    weanedCalvesSold_newValue: "",
    weanedCalvesSold_changeMetric: "",
    pricePerWeanedCalfSold: "",
    pricePerWeanedCalfSold_newValue: "",
    pricePerWeanedCalfSold_changeMetric: "",
    numberOfMortalitiesCalves: "",
    numberOfMortalitiesCalves_newValue: "",
    numberOfMortalitiesCalves_changeMetric: "",
    averageWeightAtMortalityCalves: "",
    averageWeightAtMortalityCalves_newValue: "",
    averageWeightAtMortalityCalves_changeMetric: "",
  }
}

const getEmissions = (): CattleEmission => {
  return {
    methaneEntericFermentation: '',
    methaneEntericFermentation_newValue: '',
    methaneEntericFermentation_changeMetric: '',
    nmvoc: '',
    nmvoc_newValue: '',
    nmvoc_changeMetric: '',
    methaneMms: '',
    methaneMms_newValue: '',
    methaneMms_changeMetric: '',
    nitrousOxideLeaching: '',
    nitrousOxideLeaching_newValue: '',
    nitrousOxideLeaching_changeMetric: '',
    nitrousOxideDirect: '',
    nitrousOxideDirect_newValue: '',
    nitrousOxideDirect_changeMetric: '',
    nitrousOxideVolatilization: '',
    nitrousOxideVolatilization_newValue: '',
    nitrousOxideVolatilization_changeMetric: '',
    nitrogenOxidesStorage: '',
    nitrogenOxidesStorage_newValue: '',
    nitrogenOxidesStorage_changeMetric: '',
    ammoniaStorage: '',
    ammoniaStorage_newValue: '',
    ammoniaStorage_changeMetric: '',
    ammoniaHousing: '',
    ammoniaHousing_newValue: '',
    ammoniaHousing_changeMetric: '',
    ammoniaYard: '',
    ammoniaYard_newValue: '',
    ammoniaYard_changeMetric: '',
    pm2_5: '',
    pm2_5_newValue: '',
    pm2_5_changeMetric: '',
    pm10: '',
    pm10_newValue: '',
    pm10_changeMetric: '',
    tsp: '',
    tsp_newValue: '',
    tsp_changeMetric: '',
  };
};

const initGrowingStageData = (): BeefGrowingStageDataIntervention => {
  return {
    input: getGrowingInput(),
    feed: getFeed(),
    housing: getHousingAndManure(),
    output: getOutput(),
    emissions: getEmissions()
  };
};

const initBreedingStageData = (): BeefBreedingStageDataIntervention => {
  return {
    input: getBreedingInput(),
    feed: {
      cows: getFeed(),
      calves: {
        ...getFeed(),
        mothersMilk: getMothersMilk()
      },
    },
    housing: getHousing(),
    output: {
      cows: getOutput(),
      calves: getOutputCalves(),
    },
    emissions: {
      cows: getEmissions(),
      calves: getEmissions(),
    }
  };
};

const initStageData = (stageType: string) => {
  if (stageType === StageType.Breeding)
    return initBreedingStageData();

  return initGrowingStageData();
};

const mapInternalSources = (
  interventionIntSource: InternalSource[],
  tempIntSource: CattleAnimalInputDBModel[],
) => {
  tempIntSource?.forEach((item) => {
    const inputData = {
      id: item.id || uuidv4(),
      distanceTruck: item.transportDistance,
      numberAnimals: item.numberOfAnimals,
      origin: item.origin,
      shrinkingRate: item.shrinkingRate,
      farmId: item.originFarmId,
      farmName: item.originFarmName,
      originStageId: item.originStageId,
      stageName: item.originStageName,
      baselineRef: item.baselineRef,
      animalType: item.animalType,
      animalSystemType: item.animalSystemType,
      stageType: item.stageType,
    };

    if (isValidValue(item.transportDistance))
      setInterventionFloatValue(
        item,
        "transportDistance",
        inputData,
        "distanceTruck",
        getUserTransportUnit(item.transportDistance),
        getDefaultTransportUnit(item.transportDistance)
      );

    if (isValidValue(item.numberOfAnimals))
      setInterventionFloatValue(
        item,
        "numberOfAnimals",
        inputData,
        "numberAnimals"
      );

    if (isValidValue(item.shrinkingRate)) {
      setInterventionFloatValue(
        item,
        "shrinkingRate",
        inputData,
        "shrinkingRate"
      );
    }

    interventionIntSource.push(inputData as InternalSource);
  });
};

const mapExternalSources = (
  interventionExtSource: ExternalSource[],
  tempExtSource: CattleAnimalInputDBModel[],
) => {
  tempExtSource?.forEach((item) => {
    const externalData = {
      id: item.id || uuidv4(),
      distanceTruck: item.transportDistance,
      numberAnimals: item.numberOfAnimals,
      origin: item.origin,
      shrinkingRate: item.shrinkingRate,
    };

    if (isValidValue(item.transportDistance))
      setInterventionFloatValue(
        item,
        "transportDistance",
        externalData,
        "distanceTruck"
      );

    if (isValidValue(item.numberOfAnimals))
      setInterventionFloatValue(
        item,
        "numberOfAnimals",
        externalData,
        "numberAnimals"
      );

    if (isValidValue(item.shrinkingRate))
      setInterventionFloatValue(
        item,
        "shrinkingRate",
        externalData,
        "shrinkingRate"
      );

    interventionExtSource.push(externalData as ExternalSource);
  });
};

const mapCommonInput = (
  interventionInput: BeefInputBreedingIntervention | BeefInputGrowingIntervention,
  tempInput: BeefInputBreedingInterventionDBModel | BeefInputGrowingInterventionDBModel,
) => {
  if (isValidValue(tempInput.averageWeightNewAnimals)) {
    setInterventionFloatValue(
      tempInput,
      "averageWeightNewAnimals",
      interventionInput,
      "averageWeightNewAnimals"
    );
  }

  if (isValidValue(tempInput.animalsPresentAtStart)) {
    setInterventionFloatValue(
      tempInput,
      "animalsPresentAtStart",
      interventionInput,
      "animalsPresentAtStart"
    );
  }

  if (isValidValue(tempInput.animalsPresentAtEnd)) {
    setInterventionFloatValue(
      tempInput,
      "animalsPresentAtEnd",
      interventionInput,
      "animalsPresentAtEnd"
    );
  }

  if (isValidValue(tempInput.averageWeightAtStart)) {
    setInterventionFloatValue(
      tempInput,
      "averageWeightAtStart",
      interventionInput,
      "averageWeightAtStart"
    );
  }

  interventionInput["cattleProductivity"] = tempInput["animalProductivity"];

  mapInternalSources(interventionInput.internalSources, tempInput.internalSources);
  mapExternalSources(interventionInput.externalSources, tempInput.externalSources);
};

const mapBreedingInput = (
  interventionInput: BeefInputBreedingIntervention,
  tempInput: BeefInputBreedingInterventionDBModel
) => {
  const properties: (keyof BeefInputBreedingInterventionDBModel)[] = [
    "averageWeightAtBirth",
    "permanencePeriod"
  ];

  properties.forEach((property) => {
    if (isValidValue(tempInput[property])) {
      setInterventionFloatValue(
        tempInput,
        property,
        interventionInput,
        property
      );
    }
  });

  mapCommonInput(interventionInput, tempInput);
};

const mapGrowingInput = (
  interventionInput: BeefInputGrowingIntervention,
  tempInput: BeefInputGrowingInterventionDBModel
) => {
  const properties: (keyof BeefInputGrowingInterventionDBModel)[] = [
    "isStageRepeated",
    "growingOperation",
    "numberOfRepetitions",
    "growingPurpose",
    "averageAgeAtStart",
    "averageWeightAtEnd"
  ];

  properties.forEach((property) => {
    if (isValidValue(tempInput[property])) {
      setInterventionFloatValue(
        tempInput,
        property,
        interventionInput,
        property
      )
    }
  });

  mapCommonInput(interventionInput, tempInput);
};

const mapCompoundFeeds = (
  interventionFeed: CattleFeed,
  tempFeed: FeedingProgramCalfDBModel,
  userUOM: any
) => {
  if (tempFeed.compoundFeeds && Array.isArray(tempFeed.compoundFeeds)) {
    tempFeed.compoundFeeds?.forEach((item: CompoundFeedInfoDBModel) => {
      const compoundFeed = {
        id: item.compoundFeedId,
        feedType: item.name,
        kgPerAnimal: '',
        origin: item.feedmillLocation
      };

      setInterventionFloatValue(
        item,
        'id',
        compoundFeed,
        'id',
      );

      if (isValidValue(item.quantity))
        setInterventionFloatValue(
          item,
          'quantity',
          compoundFeed,
          'kgPerAnimal',
          userUOM.unitBarnOutputMass,
          defaultUnitsV2.unitBarnOutputMass
        );

      interventionFeed.compoundFeeds?.push(compoundFeed as InterventionFeed);
    }
    );
  }

  if (tempFeed.compoundFeedsAdditions && Array.isArray(tempFeed.compoundFeedsAdditions)) {
    tempFeed.compoundFeedsAdditions?.forEach((item: BaselineCompoundFeedInfoDBModel) => {
      if (item.quantity) {
        const compoundFeedAddition = {
          id: item.compoundFeedId || uuidv4(),
          feedType: item.name,
          kgPerAnimal: floatValueInDefaultUnit(
            item.quantity,
            userUOM.unitBarnOutputMass,
            defaultUnitsV2.unitBarnOutputMass
          ),
        };

        interventionFeed.compoundFeedsAdditions?.push(compoundFeedAddition as BaselineFeed);
      }
    });
  }
};

const mapSingleIngredients = (
  interventionFeed: CattleFeed,
  tempFeed: FeedingProgramCalfDBModel,
  userUOM: any,
) => {
  if (tempFeed.singleIngredients && Array.isArray(tempFeed.singleIngredients)) {
    tempFeed.singleIngredients?.forEach((item: SingleIngredientUseDBModel) => {
      if (item.quantity) {
        const singleIngredient = {
          feedType: item.ingredient.name,
          kgPerAnimal: '',
          origin: item.ingredient.origin,
        };

        if (isValidValue(item.quantity))
          setInterventionFloatValue(
            item,
            'quantity',
            singleIngredient,
            'kgPerAnimal',
            userUOM.unitBarnOutputMass,
            defaultUnitsV2.unitBarnOutputMass
          );

        interventionFeed.singleIngredients?.push(singleIngredient as InterventionFeed);
      }
    });
  }

  if (tempFeed.singleIngredientsAdditions && Array.isArray(tempFeed.singleIngredientsAdditions)) {
    tempFeed.singleIngredientsAdditions?.forEach((item: BaselineSingleIngredientUseDBModel) => {
      if (item.quantity) {
        const singleIngredientAddition = {
          id: item.ingredient.id || uuidv4(),
          feedType: item.ingredient.name,
          origin: item.ingredient.origin,
          kgPerAnimal: floatValueInDefaultUnit(
            item.quantity,
            userUOM.unitBarnOutputMass,
            defaultUnitsV2.unitBarnOutputMass
          )
        };

        interventionFeed.singleIngredientsAdditions?.push(singleIngredientAddition as BaselineFeedWithOrigin);
      }
    });
  }
};

const mapFreshGrass = (
  interventionFeed: CattleFeed,
  tempFeed: FeedingProgramCalfDBModel
) => {
  if (tempFeed.freshGrass && Array.isArray(tempFeed.freshGrass)) {
    const properties: (keyof FreshGrassDBModel)[] = [
      "amount",
      "crudeProteinContent",
      "dryMatterContent",
      "digestibility",
      "neutralDetergentFiber",
      "grossEnergyContent",
      "ashContent",
    ];

    tempFeed.freshGrass?.forEach((item: FreshGrassDBModel) => {
      const freshGrass = {
        type: item.type as string,
        amount: {},
        crudeProteinContent: {},
        dryMatterContent: {},
        digestibility: {},
        neutralDetergentFiber: {},
        grossEnergyContent: {},
        ashContent: {}
      };

      properties.forEach((property) => {
        if (isValidValue(item[property])) {
          setInterventionFloatValue(
            item,
            property,
            freshGrass,
            property
          )
        }
      });

      interventionFeed.freshGrass?.push(freshGrass as FreshGrass);
    });
  }

  if (tempFeed.freshGrassAdditions && Array.isArray(tempFeed.freshGrassAdditions)) {
    tempFeed.freshGrassAdditions?.forEach((item: BaselineFreshGrassDBModel) => {
      const grassAddition = {
        type: item.type,
        amount: floatValueInDefaultUnit(item.amount),
        crudeProteinContent: floatValueInDefaultUnit(item.crudeProteinContent),
        dryMatterContent: floatValueInDefaultUnit(item.dryMatterContent),
        digestibility: floatValueInDefaultUnit(item.digestibility),
        neutralDetergentFiber: floatValueInDefaultUnit(item.neutralDetergentFiber),
        grossEnergyContent: floatValueInDefaultUnit(item.grossEnergyContent),
        ashContent: floatValueInDefaultUnit(item.ashContent),
      };

      interventionFeed.freshGrassAdditions?.push(grassAddition);
    }
    );
  }
};

const mapMothersMilk = (
  interventionFeed: CattleFeed,
  tempFeed: FeedingProgramCalfDBModel,
) => {
  if (tempFeed.mothersMilk) {
    const properties: (keyof MothersMilkDBModel)[] = [
      "amount",
      "crudeProteinContent",
      "dryMatterContent",
      "digestibility",
    ];

    properties.forEach((property) => {
      if (isValidValue(tempFeed.mothersMilk![property])) {
        setInterventionFloatValue(
          tempFeed.mothersMilk,
          property,
          interventionFeed.mothersMilk,
          property
        );
      }
    });
  }
};

const mapFeed = (
  interventionFeed: CattleFeed,
  tempFeed: FeedingProgramCalfDBModel,
) => {
  const userPrefs = UserProfilePrefs.getInstance();
  const userUOM = userPrefs.getUserUnitPrefs();

  interventionFeed["dietCharacterisation"] = tempFeed["dietCharacterisation"];

  setInterventionFloatValue(
    tempFeed,
    "averageMilkFat",
    interventionFeed,
    "averageMilkFat"
  );

  mapCompoundFeeds(interventionFeed, tempFeed, userUOM);
  mapSingleIngredients(interventionFeed, tempFeed, userUOM);
  mapFreshGrass(interventionFeed, tempFeed);
  mapMothersMilk(interventionFeed, tempFeed);
};

const mapBeddingType = (
  interventionHousingAndManure: CattleHousingAndManure | CattleHousing,
  tempHousingAndManure: CattleHousingAndManureDBModel | CattleHousingDBModel,
) => {
  const userPrefs = UserProfilePrefs.getInstance();
  const userUOM = userPrefs.getUserUnitPrefs();
  const outputMassPrecision = userPrefs.getUnitOutputMassPrecision();
  const { beddingSystems, beddingSystemsAdditions } = tempHousingAndManure;

  if (beddingSystems && Array.isArray(beddingSystems)) {
    beddingSystems.forEach((item) => {
      if (item) {
        const beddingType = {
          beddingType: item.type,
          beddingAmount: ''
        }

        if (isValidValue(item.amount))
          setInterventionFloatValue(
            item,
            "amount",
            beddingType,
            "beddingAmount",
            userUOM.unitOutputMass,
            defaultUnitsV2.unitOutputMass,
            outputMassPrecision
          );

        interventionHousingAndManure?.beddingSystems?.push(beddingType as CattleBeddingSystem);
      }
    });
  }

  if (
    beddingSystemsAdditions && Array.isArray(beddingSystemsAdditions)) {
    beddingSystemsAdditions.forEach((item) => {
      if (item) {
        const beddingAddition = {
          beddingType: item.type,
          beddingAmount: floatValueInDefaultUnit(
            item.amount,
            userUOM.unitBarnOutputMass,
            defaultUnitsV2.unitBarnOutputMass
          ),
        }

        interventionHousingAndManure?.beddingSystemsAdditions?.push(beddingAddition as BaselineCattleBeddingSystem);
      }
    });
  }
};

const mapMMSType = (
  intHousingAndManure: CattleHousingAndManure,
  tempHousingAndManure: CattleHousingAndManureDBModel,
) => {
  setInterventionFloatValue(
    tempHousingAndManure,
    "timeInHousing",
    intHousingAndManure,
    "timeInHousing",
  );

  setInterventionFloatValue(
    tempHousingAndManure,
    "timeInFeedlot",
    intHousingAndManure,
    "timeInFeedlot",
  );

  setInterventionFloatValue(
    tempHousingAndManure,
    "timeOnLargeGrazingAreas",
    intHousingAndManure,
    "timeGrazingLargeAreas",
  );

  setInterventionFloatValue(
    tempHousingAndManure,
    "timeOnSmallGrazingAreas",
    intHousingAndManure,
    "timeGrazingPastures",
  );

  if (tempHousingAndManure.manureSystems && Array.isArray(tempHousingAndManure.manureSystems)) {
    tempHousingAndManure.manureSystems.forEach((item) => {
      if (item) {
        const mmsType = {
          id: item.id || uuidv4(),
          share: '',
          mmsType: item.mmsType,
          storagePeriod: '',
          manurePhase: '',
        };

        setInterventionFloatValue(
          item,
          "storagePeriod",
          mmsType,
          "storagePeriod",
        );

        if (isValidValue(item.share))
          setInterventionFloatValue(
            item,
            "share",
            mmsType,
            "share",
          );

        setInterventionFloatValue(
          item,
          "manurePhase",
          mmsType,
          "manurePhase",
        );

        intHousingAndManure.manureSystems?.push(mmsType as CattleManureManagementSystem);
      }
    }
    )
  }

  if (
    tempHousingAndManure.manureSystemsAdditions && Array.isArray(tempHousingAndManure.manureSystemsAdditions)) {
    tempHousingAndManure.manureSystemsAdditions.forEach((item) => {
      if (item) {
        intHousingAndManure.manureSystemsAdditions?.push({
          id: item.id,
          mmsType: item.mmsType as CattleManureManagementSystemType,
          storagePeriod: item.storagePeriod,
          share: floatValueInDefaultUnit(item.share),
          manurePhase: floatValueInDefaultUnit(item.manurePhase),
        });
      }
    });
  }
};

const mapHousingAndManure = (
  interventionHousingAndManure: CattleHousingAndManure,
  tempHousingAndManure: CattleHousingAndManureDBModel,
) => {
  mapBeddingType(interventionHousingAndManure, tempHousingAndManure);
  mapMMSType(interventionHousingAndManure, tempHousingAndManure);
}

const mapHousing = (
  interventionHousing: CattleHousing,
  tempHousing: CattleHousingDBModel,
) => {
  mapBeddingType(interventionHousing, tempHousing);
  mapMMSType(interventionHousing.calves, tempHousing.calves);
  mapMMSType(interventionHousing.cows, tempHousing.cows);
};

const mapOutput = (
  interventionOutput: BeefOutputIntervention,
  tempOutput: BeefOutputGrowingInterventionDBModel
) => {
  const properties: (keyof BeefOutputGrowingInterventionDBModel)[] = [
    "averageAge",
    "cowsToAnotherStage",
    "averageWeightOfCowsLeavingTheStage",
    "cowsSold",
    "pricePerCowSold",
    "numberOfMortalitiesCows",
    "averageWeightAtMortalityCows",
  ];

  properties.forEach((property) => {
    if (isValidValue(tempOutput[property])) {
      setInterventionFloatValue(
        tempOutput,
        property,
        interventionOutput,
        property
      )
    }
  });
};

const mapOutputCalves = (
  interventionOutput: BeefOutputBreedingCalvesIntervention,
  tempOutput: BeefOutputBreedingCalvesDBModel
) => {
  const properties: (keyof BeefOutputBreedingCalvesDBModel)[] = [
    "weanedCalvesToAnotherStage",
    "averageWeightOfCalvesLeavingTheStage",
    "weanedCalvesSold",
    "pricePerWeanedCalfSold",
    "numberOfMortalitiesCalves",
    "averageWeightAtMortalityCalves",
  ];

  properties.forEach((property) => {
    setInterventionFloatValue(
      tempOutput,
      property,
      interventionOutput,
      property
    )
  });
};

const mapEmissions = (
  interventionEmission: CattleEmission,
  tempEmission: EmissionsDBModel,
) => {
  const { emissionMitigations } = tempEmission;

  if (emissionMitigations && emissionMitigations.length !== 0) {
    emissionMitigations.forEach((item) => {
      const { emissionType, change } = item;

      if (change && emissionType) {
        const baseKey = emissionType as keyof CattleEmission;

        if (change.value !== undefined && change.value !== null)
          (interventionEmission[baseKey] as InterventionPropertyValue) = change.value;

        const changeTypeKey = emissionType.concat("_changeMetric") as keyof CattleEmission;
        interventionEmission[changeTypeKey] = change.change_type;
      }
      else
        return;
    });
  }
  else
    return;
};

const mapStageInterventionDataInSustell = (stageBlockData: BeefStageDBModel) => {
  const { stageData } = stageBlockData;

  const oneStageData = {
    id: stageBlockData?.id || uuidv4(),
    name: stageBlockData?.name,
    type: stageBlockData.type,
    stageData: { ...initStageData(stageBlockData.type as string) },
  };

  if (stageData) {
    if (stageBlockData.type === StageType.Breeding) {
      const beefBreedingStageData = stageData as BeefBreedingStageDataInterventionDBModel;
      const { input, feed, housing, output, emissions } = oneStageData.stageData as BeefBreedingStageDataIntervention;

      if (input && beefBreedingStageData.input)
        mapBreedingInput(input, beefBreedingStageData.input);

      if (feed && beefBreedingStageData.feed) {
        mapFeed(feed.cows, beefBreedingStageData.feed.cows);
        mapFeed(feed.calves, beefBreedingStageData.feed.calves);
      }

      if (housing && beefBreedingStageData.housing)
        mapHousing(housing, beefBreedingStageData.housing);

      if (output && beefBreedingStageData.output) {
        if (output.cows && beefBreedingStageData.output.cows)
          mapOutput(output.cows, beefBreedingStageData.output.cows);

        if (output.calves && beefBreedingStageData.output.calves)
          mapOutputCalves(output.calves, beefBreedingStageData.output.calves);
      }

      if (emissions && beefBreedingStageData.emissions) {
        if (emissions.cows && beefBreedingStageData.emissions.cows)
          mapEmissions(emissions.cows, beefBreedingStageData.emissions.cows);

        if (emissions.calves && beefBreedingStageData.emissions.calves)
          mapEmissions(emissions.calves, beefBreedingStageData.emissions.calves);
      }
    }

    if (stageBlockData.type === StageType.Growing) {
      const beefGrowingStageData = stageData as BeefGrowingStageDataInterventionDBModel;
      const { input, feed, housing, output, emissions } = oneStageData.stageData as BeefGrowingStageDataIntervention;

      if (input && beefGrowingStageData.input)
        mapGrowingInput(input, beefGrowingStageData.input);

      if (feed && beefGrowingStageData.feed)
        mapFeed(feed, beefGrowingStageData.feed);

      if (housing && beefGrowingStageData.housing)
        mapHousingAndManure(housing, beefGrowingStageData.housing);

      if (output && beefGrowingStageData.output)
        mapOutput(output, beefGrowingStageData.output);

      if (emissions && beefGrowingStageData.emissions)
        mapEmissions(emissions, beefGrowingStageData.emissions);
    }
  }

  return oneStageData;
};

const mapBeefInterventionDataInSustell = (inDataObj: BeefInterventionDBModel) => {
  if (inDataObj) {
    let dataObj: BeefInterventionDBModel = { ...inDataObj, stages: [] };
    inDataObj.stages?.forEach((stage: BeefStageDBModel) => {
      let stageNew: BeefStageDBModel;
      if (stage.stageData && typeof stage.stageData === "string") {
        stageNew = { ...stage, stageData: JSON.parse(stage.stageData) || {} };
      } else {
        stageNew = { ...stage };
      }
      dataObj.stages?.push(stageNew);
    });

    const payloadObj = createCoreForm(dataObj);

    if (dataObj.resourceUse)
      mapResourceUse(dataObj.resourceUse, payloadObj.resourceUse);

    if (dataObj.stages && Array.isArray(dataObj.stages)) {
      return dataObj.stages.reduce(
        (payload, stage) => {
          const mappedStage = mapStageInterventionDataInSustell(stage);

          return {
            ...payload,
            stages: [...payload.stages, mappedStage],
          };
        },
        { ...payloadObj }
      );
    }

    return payloadObj;
  }

  return {};
};

export default mapBeefInterventionDataInSustell;
